import { useState, useCallback, useMemo } from 'react';

/**
 * Hook de UI para CustomerDetail - Capa de interfaz de usuario
 * 
 * RESPONSABILIDADES:
 * - Gestionar estado visual (viewMore)
 * - Handlers de UI (toggles, clics)
 * - Estado de presentación
 * 
 * NO CONTIENE:
 * - Lógica de datos (API calls)
 * - Lógica de negocio (formateo, procesamiento)
 * - Llamadas externas
 * 
 * @returns {Object} Objeto con estados, handlers y configuraciones memoizadas
 * @returns {boolean} returns.viewMore - Estado de expansión del componente (true = expandido)
 * @returns {Function} returns.toggleViewMore - Handler para alternar estado de expansión
 * @returns {Function} returns.showMore - Handler para expandir vista (fuerza true)
 * @returns {Function} returns.showLess - Handler para colapsar vista (fuerza false)
 * @returns {Object} returns.viewToggleConfig - Configuración memoizada del botón de toggle
 * @returns {Object} returns.editButtonConfig - Configuración memoizada del botón de edición
 * @returns {Object} returns.toggleButtonConfig - Configuración memoizada del botón de expansión
 * 
 * @example Uso básico
 * const { viewMore, viewToggleConfig, editButtonConfig } = useCustomerDetailUI();
 * 
 * @example Control manual de expansión
 * const { showMore, showLess, viewMore } = useCustomerDetailUI();
 * // Para expandir manualmente
 * const handleExpand = () => showMore();
 * // Para colapsar manualmente  
 * const handleCollapse = () => showLess();
 * 
 * @example Uso con ButtonComponent
 * <ButtonComponent
 *   {...editButtonConfig}
 *   onClick={() => console.log('Edit clicked')}
 * />
 * <ButtonComponent
 *   {...toggleButtonConfig}
 *   text={t(viewToggleConfig.text)}
 *   icon={viewToggleConfig.icon}
 *   onClick={viewToggleConfig.onClick}
 * />
 */
function useCustomerDetailUI() {
  // Estado visual del componente
  const [viewMore, setViewMore] = useState(false);

  /**
   * Handler memoizado para toggle de vista expandida/colapsada
   * 
   * @function toggleViewMore
   * @description Alterna el estado viewMore entre true y false
   * @returns {void}
   */
  const toggleViewMore = useCallback(() => {
    setViewMore(prev => !prev);
  }, []);

  /**
   * Handler memoizado para forzar vista expandida
   * 
   * @function showMore
   * @description Establece viewMore en true, mostrando información adicional
   * @returns {void}
   */
  const showMore = useCallback(() => {
    setViewMore(true);
  }, []);

  /**
   * Handler memoizado para forzar vista colapsada
   * 
   * @function showLess  
   * @description Establece viewMore en false, ocultando información adicional
   * @returns {void}
   */
  const showLess = useCallback(() => {
    setViewMore(false);
  }, []);

  /**
   * Configuración memoizada para el botón de expansión/colapso
   * 
   * @type {Object}
   * @property {string} text - Key de traducción que cambia según viewMore
   * @property {string} icon - Icono que cambia según viewMore (up/down arrow)
   * @property {Function} onClick - Handler toggleViewMore memoizado
   */
  const viewToggleConfig = useMemo(() => ({
    text: viewMore ? 'customerDetails.showLess' : 'customerDetails.showMore',
    icon: viewMore ? 'arrow-ios-upward-outline' : 'arrow-ios-downward-outline',
    onClick: toggleViewMore
  }), [viewMore, toggleViewMore]);

  /**
   * Configuración memoizada para el botón de edición
   * 
   * @type {Object}
   * @property {boolean} outlined - Estilo outlined del botón
   * @property {string} size - Tamaño 'xs'
   * @property {string} variant - Variante 'SECONDARY'
   * @property {string} icon - Icono 'edit-outline'
   * @property {string} className - Clases CSS para el botón
   * @property {string} iconClassName - Clases CSS para el icono
   */
  const editButtonConfig = useMemo(() => ({
    outlined: true,
    size: 'xs',
    variant: 'SECONDARY',
    icon: 'edit-outline',
    className: 'tw-border-0 tw-bg-transparent hover:tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 !tw-p-0',
    iconClassName: 'tw-text-gray-600 !tw-w-4 !tw-h-4 tw-flex-shrink-0'
  }), []);

  /**
   * Configuración memoizada para el botón de toggle (show more/less)
   * 
   * @type {Object}
   * @property {boolean} outlined - Estilo outlined del botón
   * @property {string} size - Tamaño 'xs'
   * @property {string} variant - Variante 'SECONDARY'  
   * @property {string} className - Clases CSS para el botón
   * @property {string} iconClassName - Clases CSS para el icono
   */
  const toggleButtonConfig = useMemo(() => ({
    outlined: true,
    size: 'xs',
    variant: 'SECONDARY',
    className: 'tw-border-0 tw-bg-transparent hover:tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-px-2 tw-py-1',
    iconClassName: '!tw-w-4 !tw-h-4 tw-flex-shrink-0 tw-text-blue-500'
  }), []);

  // Retorno memoizado para evitar re-renders
  return useMemo(() => ({
    // Estados
    viewMore,
    
    // Handlers
    toggleViewMore,
    showMore,
    showLess,
    
    // Configuraciones
    viewToggleConfig,
    editButtonConfig,
    toggleButtonConfig
  }), [
    viewMore,
    toggleViewMore,
    showMore,
    showLess,
    viewToggleConfig,
    editButtonConfig,
    toggleButtonConfig
  ]);
}

export default useCustomerDetailUI;